import { useEffect, useRef } from 'react';

/**
 * Cuenta un número hacia arriba cuando entra en pantalla.
 *
 * Devuelve una ref para el nodo que contiene la cifra. El valor FINAL se
 * renderiza siempre en el HTML: la animación solo sobrescribe el texto
 * mientras dura. Así, sin JS (o en el HTML pre-renderizado que leen Google
 * y los buscadores con IA) la cifra real ya está ahí — nunca un "0" ni un
 * hueco. Con motion reducido no se anima: se queda el número final.
 *
 * Por eso el texto NO se pone en "0" al montar, aunque eso evitaría el
 * parpadeo del valor final en el caso raro de que la franja ya esté en
 * pantalla al cargar: scripts/prerender.js guarda el HTML con un navegador
 * headless de verdad, así que los efectos SÍ corren y el observador no
 * llega a dispararse (la franja queda bajo el pliegue) — se comprobó que
 * el HTML estático terminaba publicando "0 años" y "+0 toneladas". La cifra
 * arranca en 0 en el primer fotograma de la animación, no antes.
 */
export function useCountUp(target, duration = 1700) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

        let raf = 0;
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;
                observer.disconnect();

                const start = performance.now();
                const tick = (now) => {
                    const t = Math.min(1, (now - start) / duration);
                    // Desaceleración fuerte, de la misma familia que
                    // --ease-out-expo: arranca rápido y se asienta en la cifra.
                    const eased = 1 - Math.pow(1 - t, 4);
                    el.textContent = String(Math.round(target * eased));
                    if (t < 1) raf = requestAnimationFrame(tick);
                    else el.textContent = String(target);
                };
                raf = requestAnimationFrame(tick);
            },
            { threshold: 0.4 },
        );
        observer.observe(el);

        return () => {
            observer.disconnect();
            cancelAnimationFrame(raf);
        };
    }, [target, duration]);

    return ref;
}
